import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, PaperPlaneTilt, CheckCircle, CircleNotch } from '@phosphor-icons/react';

const projectTypes = [
  'New Home Build',
  'Renovation/Extension',
  'Commercial Fitout',
  'Gaming Room Renovation',
  'Bar Construction',
  'Kitchen Upgrade',
  'Maintenance/Rectification',
  'Re-Roofing',
  'Other',
];

const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      window.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    try {
      // Save to database
      await fetch(`${BACKEND_URL}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      // Send email via FormSubmit (free, no API key needed)
      // First submission triggers a verification email to rimsieconstructions@hotmail.com
      // After verification, all future submissions go straight to email
      const formPayload = new FormData();
      formPayload.append('name', formData.name);
      formPayload.append('phone', formData.phone);
      formPayload.append('email', formData.email);
      formPayload.append('projectType', formData.projectType);
      formPayload.append('message', formData.message);
      formPayload.append('_subject', `New Enquiry from ${formData.name} - Rimsie Constructions Pty Ltd`);
      formPayload.append('_captcha', 'false');
      formPayload.append('_template', 'table');

      await fetch('https://formsubmit.co/ajax/rimsieconstructions@hotmail.com', {
        method: 'POST',
        body: formPayload,
      });

      setIsSuccess(true);
      setFormData({ name: '', phone: '', email: '', projectType: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or call Brett on 0416 283 443.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setIsSuccess(false);
    setError('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={resetAndClose}
          data-testid="quote-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="bg-secondary-bg w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="quote-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-secondary-bg z-10 flex items-center justify-between p-6 pb-4 border-b border-border-subtle">
              <div>
                <p className="overline mb-1">Free Quote</p>
                <h3 className="text-heading text-2xl font-heading">Get a Free Quote</h3>
              </div>
              <button
                onClick={resetAndClose}
                data-testid="quote-modal-close"
                className="w-10 h-10 flex items-center justify-center text-heading/70 hover:text-heading transition-colors"
                aria-label="Close form"
              >
                <X size={22} weight="light" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {isSuccess ? (
                <div className="text-center py-8" data-testid="quote-form-success">
                  <CheckCircle size={56} className="text-accent mx-auto mb-4" />
                  <h3 className="text-heading text-xl font-heading mb-2">Thank You!</h3>
                  <p className="text-body-text mb-6">Your enquiry has been sent. Brett will be in touch shortly.</p>
                  <button onClick={resetAndClose} className="btn-primary">
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                  <div>
                    <label htmlFor="q-name" className="block text-heading text-sm font-medium mb-2">Full Name *</label>
                    <input type="text" id="q-name" name="name" value={formData.name} onChange={handleChange} required
                      data-testid="quote-input-name"
                      className="w-full bg-primary-bg border border-border-subtle px-4 py-3 text-heading placeholder:text-body-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
                      placeholder="Your name" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="q-phone" className="block text-heading text-sm font-medium mb-2">Phone *</label>
                      <input type="tel" id="q-phone" name="phone" value={formData.phone} onChange={handleChange} required
                        data-testid="quote-input-phone"
                        className="w-full bg-primary-bg border border-border-subtle px-4 py-3 text-heading placeholder:text-body-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
                        placeholder="0400 000 000" />
                    </div>
                    <div>
                      <label htmlFor="q-email" className="block text-heading text-sm font-medium mb-2">Email *</label>
                      <input type="email" id="q-email" name="email" value={formData.email} onChange={handleChange} required
                        data-testid="quote-input-email"
                        className="w-full bg-primary-bg border border-border-subtle px-4 py-3 text-heading placeholder:text-body-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
                        placeholder="you@email.com" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="q-projectType" className="block text-heading text-sm font-medium mb-2">Project Type *</label>
                    <select id="q-projectType" name="projectType" value={formData.projectType} onChange={handleChange} required
                      data-testid="quote-input-project-type"
                      className="w-full bg-primary-bg border border-border-subtle px-4 py-3 text-heading focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors appearance-none cursor-pointer">
                      <option value="" disabled>Select project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="q-message" className="block text-heading text-sm font-medium mb-2">Message</label>
                    <textarea id="q-message" name="message" value={formData.message} onChange={handleChange} rows={4}
                      data-testid="quote-input-message"
                      className="w-full bg-primary-bg border border-border-subtle px-4 py-3 text-heading placeholder:text-body-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors resize-none"
                      placeholder="Tell us about your project..." />
                  </div>

                  {error && <p className="text-red-400 text-sm" data-testid="quote-form-error">{error}</p>}

                  <button type="submit" disabled={isSubmitting} data-testid="quote-form-submit"
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? (
                      <><CircleNotch size={20} className="animate-spin" />Sending...</>
                    ) : (
                      <><PaperPlaneTilt size={20} />Send My Enquiry</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuoteModal;
